import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ROLES, homePathFor } from '@/features/auth/roles';
import { StorePausedScreen } from '@/features/subscription/components/StorePausedScreen';
import { RouteFallback } from '@/ui/RouteFallback';

/**
 * Route guard.
 *
 * This is convenience, not security: it decides what to render, while the API
 * decides what is permitted. A user who edits their way past this still gets
 * 403s from every endpoint, because the server checks the role on every request.
 */
export function ProtectedRoute({ allowedRoles }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Wait for /me before deciding, or a refresh would bounce a signed-in user
  // to the login screen for a moment.
  if (isLoading) {
    return <RouteFallback />;
  }

  if (!isAuthenticated) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // An admin-issued password blocks everything server-side, so rendering the
  // requested page would only produce a screen of 403s. Redirect first.
  if (user.must_change_password && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  // An expired shop: the API answers 403 everywhere but the subscription page,
  // so the admin is taken there and a seller, who cannot renew, is told why.
  if (user.subscription_expired) {
    if (user.role === ROLES.ADMIN && location.pathname !== SUBSCRIPTION_PATH) {
      return <Navigate to={SUBSCRIPTION_PATH} replace />;
    }
    if (user.role === ROLES.VENDOR) return <StorePausedScreen />;
  }

  if (allowedRoles?.length && !allowedRoles.includes(user.role)) {
    // Send them to their own area rather than showing a dead end.
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  return <Outlet />;
}

export const CHANGE_PASSWORD_PATH = '/change-password';
export const SUBSCRIPTION_PATH = '/admin/subscription';

/**
 * Wraps the change-password screen itself.
 *
 * Signed in is the only requirement - deliberately not role-gated, because the
 * whole point is that the account cannot do anything else yet.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <RouteFallback />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;

  return <Outlet />;
}

/** Keeps signed-in users off /login and /register. */
export function PublicOnlyRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <RouteFallback />;
  if (isAuthenticated) return <Navigate to={homePathFor(user.role)} replace />;

  return <Outlet />;
}
